import React from 'react';
import { Wine, MapPin, Calendar, Truck, ChevronRight, AlertTriangle } from 'lucide-react';
import { PROMO_STATUS_LABELS, PROMO_STATUS_COLORS } from '../../../services/promoSummerOrderService';

export default function PromoOrderList({ orders, loading, onSelect, onRefresh }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3]?.map((i) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gray-200 dark:bg-slate-600 rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-1/3" />
                <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!orders?.length) {
    return (
      <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
        <Wine className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400 font-medium">Aucune commande promo trouvée</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Créez votre première commande avec le bouton ci-dessus</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders?.map((order) => {
        const statusLabel = PROMO_STATUS_LABELS?.[order?.order_status] || order?.order_status;
        const statusColor = PROMO_STATUS_COLORS?.[order?.order_status] || 'bg-gray-100 text-gray-700';
        const isRelance = order?.order_status === 'relance_necessaire';
        const totalPromo = (order?.barrel_quantity || 0) * 160;
        const totalDeposit = (order?.barrel_quantity || 0) * 50;
        const totalBalance = (order?.barrel_quantity || 0) * 110;

        return (
          <div
            key={order?.id}
            onClick={() => onSelect?.(order)}
            className={`bg-white dark:bg-slate-800 rounded-xl border transition-all cursor-pointer hover:shadow-md ${
              isRelance
                ? 'border-red-300 dark:border-red-700 hover:border-red-400' :'border-gray-200 dark:border-slate-700 hover:border-amber-300 dark:hover:border-amber-600'
            }`}
          >
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`p-2 rounded-xl flex-shrink-0 ${isRelance ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                    {isRelance ? (
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                    ) : (
                      <Wine className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 dark:text-white truncate">
                        {order?.client_name}
                      </span>
                      {order?.client_phone && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">{order?.client_phone}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                        {statusLabel}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {order?.barrel_quantity} barrique{order?.barrel_quantity > 1 ? 's' : ''} 270L
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              </div>

              {/* Details row */}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block">Total promo</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{totalPromo}€</span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block">Acompte</span>
                  <span className={`font-semibold ${order?.deposit_paid ? 'text-green-600' : 'text-orange-500'}`}>
                    {totalDeposit}€ {order?.deposit_paid ? '✓' : '—'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 dark:text-gray-500 block">Solde</span>
                  <span className={`font-semibold ${order?.balance_paid ? 'text-green-600' : 'text-blue-500'}`}>
                    {totalBalance}€ {order?.balance_paid ? '✓' : '—'}
                  </span>
                </div>
                <div>
                  {order?.delivery_date ? (
                    <>
                      <span className="text-gray-400 dark:text-gray-500 block flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> Livraison
                      </span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {new Date(order.delivery_date)?.toLocaleDateString('fr-FR')}
                      </span>
                    </>
                  ) : order?.delivery_city ? (
                    <>
                      <span className="text-gray-400 dark:text-gray-500 block flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> Ville
                      </span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{order?.delivery_city}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-400 dark:text-gray-500 block flex items-center gap-1">
                        <Truck className="h-3 w-3" /> Prestataire
                      </span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{order?.provider_name || '—'}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
